import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Match from './Match';

function MatchPage({ matches, series, tournaments, teams, socket, players, user }) {
  const location = useLocation();
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [newMatch, setNewMatch] = useState({
    type: 'series', // 'series' or 'tournament'
    id: '', // id of series or tournament
    home_team_id: '',
    away_team_id: '',
    date: ''
  });

  const handleNewMatchChange = (e) => {
    setNewMatch({ ...newMatch, [e.target.name]: e.target.value });
  };

  const [deletedMatches, setDeletedMatches] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    if (!socket) return;

    const handleDeletedMatches = (data) => {
      setDeletedMatches(data);
    };

    socket.on('deletedMatches', handleDeletedMatches);
    return () => {
      socket.off('deletedMatches', handleDeletedMatches);
    };
  }, [socket]);

  useEffect(() => {
    if (location.state?.matchId && matches.length > 0) {
      const match = matches.find((m) => String(m.id) === String(location.state.matchId));
      if (match) setSelectedMatch(match);
    }
  }, [location.state, matches]);

  useEffect(() => {
    if (!socket || !showDeleted) return;
    socket.emit('getDeletedMatches');
  }, [socket, showDeleted]);

  const deleteMatch = (matchId) => {
    if (!socket) return;
    if (!window.confirm('Poistetaanko tämä ottelu? Ottelu siirretään poistettuihin.')) return;
    socket.emit('deleteMatch', matchId);
  };

  const restoreMatch = (matchId) => {
    if (!socket) return;
    socket.emit('restoreMatch', matchId);
  };

  const addMatch = (e) => {
    e.preventDefault();
    const { type, id, home_team_id, away_team_id, date } = newMatch;
    if (!id || !home_team_id || !away_team_id || !date) {
      alert('Täytä kaikki kentät');
      return;
    }
    const matchData = {
      series_id: type === 'series' ? id : null,
      tournament_id: type === 'tournament' ? id : null,
      home_team_id,
      away_team_id,
      date
    };
    socket.emit('addMatch', matchData);
  };

  const getTeamsForSelection = () => {
    if (newMatch.id) {
      if (newMatch.type === 'series') {
        const selectedSeries = series.find(s => s.id === parseInt(newMatch.id));
        if (selectedSeries && selectedSeries.teams) {
          const teamNames = selectedSeries.teams.split(',');
          return teams.filter(t => teamNames.includes(t.name));
        }
      } else {
        const selectedTournament = tournaments.find(t => t.id === parseInt(newMatch.id));
        if (selectedTournament && selectedTournament.teams) {
          const teamNames = selectedTournament.teams.split(',');
          return teams.filter(t => teamNames.includes(t.name));
        }
      }
    }
    return [];
  };

  const availableTeams = getTeamsForSelection();
  const canEdit = user && (user.role === 'SuperAdmin' || user.role === 'Admin' || user.role === 'AdminTournament' || user.role === 'MatchEditor');

  useEffect(() => {
    if (!canEdit && showDeleted) {
      setShowDeleted(false);
    }
  }, [canEdit, showDeleted]);

  const formatMatchStatus = (status) => {
    switch (status) {
      case 'scheduled':
        return 'Aikataulutettu';
      case 'live':
        return 'Käynnissä';
      case 'finished':
        return 'Päättynyt';
      default:
        return status;
    }
  };

  const groupMatchesByStatus = (matchesArray) => {
    return {
      scheduled: matchesArray.filter(m => m.status === 'scheduled'),
      live: matchesArray.filter(m => m.status === 'live'),
      finished: matchesArray.filter(m => m.status === 'finished')
    };
  };

  const renderMatchGroup = (groupName, matchesInGroup, statusLabel) => {
    if (matchesInGroup.length === 0) return null;
    
    return (
      <div key={groupName} className={`match-status-group match-status-${groupName}`}>
        <h3 className="match-status-header">{statusLabel}</h3>
        <ul className="matches-list">
          {matchesInGroup.map(match => (
            <li key={match.id} className="match-card" onClick={() => setSelectedMatch(match)}>
              <span className="match-summary">{match.home_team_name} vastaan {match.away_team_name} - {new Date(match.date).toLocaleString()}</span>
              <div className="match-actions">
                <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedMatch(match); }}>
                  Näytä
                </button>
                {canEdit && (
                  <button type="button" className="delete-button" onClick={(e) => { e.stopPropagation(); deleteMatch(match.id); }}>
                    Poista
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  useEffect(() => {
    if (!selectedMatch) return;
    const updated = matches.find((m) => Number(m.id) === Number(selectedMatch.id));
    if (updated) {
      setSelectedMatch(updated);
    }
  }, [matches, selectedMatch]);

  if (!showDeleted && selectedMatch) {
    return <Match match={selectedMatch} back={() => setSelectedMatch(null)} socket={socket} players={players} teams={teams} user={user} />;
  }

  return (
    <div className="matches-section">
      <h1>Otteluseuranta</h1>
      <div className="matches-toolbar">
        <button type="button" onClick={() => setShowDeleted(false)}>
          Aktiiviset ottelut
        </button>
        {canEdit && (
          <button type="button" onClick={() => setShowDeleted(true)}>
            Poistetut ottelut
          </button>
        )}
      </div>
      
      {!showDeleted && canEdit && (
        <>
          <h2>Luo uusi ottelu</h2>
          <form className="match-create-form" onSubmit={addMatch}>
            <select name="type" value={newMatch.type} onChange={handleNewMatchChange}>
              <option value="series">Sarja</option>
              <option value="tournament">Turnaus</option>
            </select>
            
            {newMatch.type === 'series' ? (
              <select name="id" value={newMatch.id} onChange={handleNewMatchChange}>
                <option value="">Valitse sarja</option>
                {series.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : (
              <select name="id" value={newMatch.id} onChange={handleNewMatchChange}>
                <option value="">Valitse turnaus</option>
                {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}

            <select name="home_team_id" value={newMatch.home_team_id} onChange={handleNewMatchChange}>
              <option value="">Valitse kotijoukkue</option>
              {availableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            
            <select name="away_team_id" value={newMatch.away_team_id} onChange={handleNewMatchChange}>
              <option value="">Valitse vierasjoukkue</option>
              {availableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>

            <input type="datetime-local" name="date" value={newMatch.date} onChange={handleNewMatchChange} />
            
            <button type="submit">Luo ottelu</button>
          </form>
        </>
      )}

      <h2>{showDeleted ? 'Poistetut ottelut' : 'Ottelut'}</h2>
      {showDeleted && canEdit ? (
        <ul className="matches-list deleted-matches-list">
          {deletedMatches.map((match) => (
            <li key={match.id} className="match-card deleted-match-card">
              <span className="match-summary">{match.home_team_name} vastaan {match.away_team_name} - {formatMatchStatus(match.status)} - {new Date(match.date).toLocaleString()}</span>
              {canEdit && (
                <button type="button" className="match-restore-button" onClick={() => restoreMatch(match.id)}>
                  Palauta
                </button>
              )}
            </li>
          ))}
          {deletedMatches.length === 0 && <p className="matches-empty">Poistettuja otteluita ei löytynyt.</p>}
        </ul>
      ) : (
        <div className="matches-by-status">
          {(() => {
            const grouped = groupMatchesByStatus(matches);
            const hasAnyMatches = matches.length > 0;
            
            return (
              <>
                {renderMatchGroup('live', grouped.live, 'Käynnissä olevat ottelut')}
                {renderMatchGroup('scheduled', grouped.scheduled, 'Aikataulutetut ottelut')}
                {renderMatchGroup('finished', grouped.finished, 'Päättyneet ottelut')}
                {!hasAnyMatches && <p className="matches-empty">Otteluita ei löytynyt.</p>}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default MatchPage;
